import { consumer } from "@lib/cable";
import { useChatDataVersion } from "@lib/hooks/useChatDataVersion";
import { useEffect, useRef } from "react";

export interface BroadcastPayload {
  resource: string;
  id?: number | string;
  action: "created" | "updated" | "destroyed";
  version?: number;
}

export interface UseLiveResourceParams {
  channel: string;
  params?: Record<string, string | number>;
  onInvalidate: () => void;
  refetchOnFocus?: boolean;
  refetchOnReconnect?: boolean;
  alsoListenToChat?: boolean;
  debounceMs?: number;
}

// Standard live-update wiring: see docs/live-ui-sync.md.
//
// Bundles ActionCable subscription, debounced invalidation, version-aware
// dedup, refetch on tab visibility regained, refetch on AC reconnect, and
// optional chat dataVersion fan-in.
export function useLiveResource({
  channel,
  params,
  onInvalidate,
  refetchOnFocus = true,
  refetchOnReconnect = true,
  alsoListenToChat = true,
  debounceMs = 200,
}: UseLiveResourceParams): void {
  const onInvalidateRef = useRef(onInvalidate);
  onInvalidateRef.current = onInvalidate;

  const paramsKey = params ? JSON.stringify(params) : "";

  // biome-ignore lint/correctness/useExhaustiveDependencies: paramsKey is the stable serialisation of params; depending on params (an object) would re-subscribe on every render
  useEffect(() => {
    const seenVersions = new Map<string, number>();
    let timeoutId: ReturnType<typeof setTimeout> | undefined;
    let everConnected = false;
    let wasHidden = false;

    const fire = () => onInvalidateRef.current();

    const fireDebounced = () => {
      if (timeoutId !== undefined) clearTimeout(timeoutId);
      timeoutId = setTimeout(fire, debounceMs);
    };

    const subscription = consumer.subscriptions.create(
      { channel, ...(params ?? {}) },
      {
        received(payload: BroadcastPayload) {
          if (payload?.id != null && payload?.version != null) {
            const key = `${payload.resource}:${payload.id}`;
            const last = seenVersions.get(key);
            if (last !== undefined && payload.version <= last) return;
            seenVersions.set(key, payload.version);
          }
          fireDebounced();
        },
        connected() {
          if (!everConnected) {
            everConnected = true;
            return;
          }
          if (refetchOnReconnect) fire();
        },
      },
    );

    let visibilityHandler: (() => void) | undefined;
    if (refetchOnFocus && typeof document !== "undefined") {
      visibilityHandler = () => {
        if (document.visibilityState === "hidden") {
          wasHidden = true;
        } else if (document.visibilityState === "visible" && wasHidden) {
          wasHidden = false;
          fire();
        }
      };
      document.addEventListener("visibilitychange", visibilityHandler);
    }

    return () => {
      if (timeoutId !== undefined) clearTimeout(timeoutId);
      if (visibilityHandler) {
        document.removeEventListener("visibilitychange", visibilityHandler);
      }
      subscription.unsubscribe();
    };
  }, [channel, paramsKey, debounceMs, refetchOnFocus, refetchOnReconnect]);

  // Hooks must be called unconditionally; pass a no-op when chat fan-in is
  // disabled so the call is stable across renders.
  const noop = useRef(() => {}).current;
  useChatDataVersion(alsoListenToChat ? onInvalidate : noop);
}
